import React, { useState, useEffect } from 'react';
import { Grid, Box, Typography, IconButton, Link, Tabs, Tab } from '@mui/material';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import HelpIcon from '../../images/help.svg';
import { EventCard } from '../../components/EventCard/EventCard';
import { flexcol, flexItemCenter, flexItemLeft, flexrow } from '../../global'
import { useStyles } from './style'
import { CalendarEventQueueInterface } from './EtaCalendarPage'

interface CalendarEventSliderProps {
  role?: string;
  eventQueue: CalendarEventQueueInterface[];
  eventsTitle: string[];
  activeState: string;
  setActiveState: (state: string) => void;
  setOpenAllEvents: (open: boolean) => void;
  handleCancelEvent: (id: number) => void;
  handleRevertEvent: (id: number) => void;
}

export const CalendarEventSlider = ({
  role,
  eventQueue,
  eventsTitle,
  activeState,
  setActiveState,
  setOpenAllEvents,
  handleCancelEvent,
  handleRevertEvent
}: CalendarEventSliderProps) => {
  const classes = useStyles()
  const [preCounter, setPreCounter] = useState(1);
  const [nextCounter, setNextCounter] = useState(1);

  // Find the current active event index in eventQueue
  const currentEventIndex = activeState === 'View$$All%%Events' 
    ? eventQueue.length - 1 
    : eventQueue.findIndex(event => event.id.toString() === activeState);

  useEffect(() => {
    currentEventIndex - 2 >= 0
      ? setPreCounter(2)
      : setPreCounter(1);
    currentEventIndex + 2 <= eventQueue.length - 1
      ? setNextCounter(2)
      : setNextCounter(1);
  });

  const handlePreviousEvent = () => {
    if (currentEventIndex > 0) {
      const newIndex = Math.max(0, currentEventIndex - preCounter);
      setActiveState(eventQueue[newIndex].id.toString());
    }
  };

  const handleNextEvent = () => {
    if (currentEventIndex < eventQueue.length - 1) {
      const newIndex = Math.min(eventQueue.length - 1, currentEventIndex + nextCounter);
      setActiveState(eventQueue[newIndex].id.toString());
    } else if (currentEventIndex === eventQueue.length - 1) {
      // If we're at the last event, go to "View All Events"
      setActiveState('View$$All%%Events');
    }
  };

  return (
    <Grid item xs={12} md={6.4} lg={6.4} sm={12} style={flexcol}>
      <Box
        style={flexrow}
        justifyContent='flex-start'
        alignItems='center'
        sx={{ marginLeft: { lg: '60px' }, mb: '10px' }}
      >
        {role === undefined && (
          <Typography sx={{ fontSize: '17px', fontWeight: '700' }}>
            Upcoming Events
          </Typography>
        )}

        {eventQueue.length > 0 && (
          <>
            <IconButton
              onClick={handlePreviousEvent}
              disabled={
                eventQueue.length === 0 ||
                currentEventIndex === 0
              }
              size="large">
              <KeyboardArrowLeftIcon fontSize='small' />
            </IconButton>
            <IconButton
              onClick={handleNextEvent}
              disabled={
                eventQueue.length < 2 ||
                activeState === 'View$$All%%Events'
              }
              size="large">
              <KeyboardArrowRightIcon fontSize='small' />
            </IconButton>
          </>
        )}

        {(role === 'REQUESTER' ||
          role === 'ADMIN' ||
          role === 'APPROVER') && (
          <>
            {eventQueue.length > 0 && (
              <Link
                onClick={() => setOpenAllEvents(true)}
                sx={{
                  fontWeight: '500',
                  lineHeight: '20px',
                  cursor: 'pointer',
                  color: '#006BD7'
                }}
              >
                View My Events
              </Link>
            )}
          </>
        )}
      </Box>
      <Grid
        container
        style={flexrow}
        sx={{
          width: '100%',
          // height: '100%',
          borderLeft: {
            lg: '2px solid #E8EBF4',
            md: '2px solid #E8EBF4'
          },
          pl: { lg: '50px', md: '50px' }
        }}
      >
        {eventQueue.length > 0 ? (
          <Tabs
            value={activeState}
            // onChange={handleChange}
            variant='scrollable'
            scrollButtons={false}
            TabIndicatorProps={{
              style: { display: 'none' }
            }}
            sx={{
              backgroundColor: 'inherit',
            }}
          >
            {eventQueue.map((data: any) => {
              return (
              <Tab
                className={classes.tab}
                key={data.id}
                value={data.id.toString()}
                disableFocusRipple={true}
                disableRipple={true}
                onClick={() => setActiveState(data.id.toString())}
                sx={{
                  display: 'flex',
                  height: '100%',
                  mr: 2,
                  p: 0,
                  opacity: 1,
                  cursor: 'pointer'
                }}
                label={
                  <EventCard
                    data={data}
                    onCancelEvent={handleCancelEvent}
                    onRevertEvent={handleRevertEvent}
                  />
                }
              ></Tab>
            )
          })}

            <Tab
              className={classes.tab}
              value={'View$$All%%Events'}
              disableFocusRipple={true}
              disableRipple={true}
              onClick={() => setActiveState('View$$All%%Events')}
              sx={{
                display: 'flex',
                height: '100%',
                mr: 2,
                p: 0,
                cursor: 'pointer'
              }}
              label={
                <Box
                  sx={{
                    width: {
                      lg: '228px',
                      md: '210px',
                      sm: '210px',
                      xs: '210px'
                    },
                    height: '165px',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center'
                  }}
                  style={flexcol}
                >
                  <Box
                    onClick={() => setOpenAllEvents(true)}
                    sx={{
                      width: '200px',
                      borderRadius: '24px',
                      backgroundColor: '#333333',
                      py: '10px',
                      cursor: 'pointer'
                    }}
                  >
                    <Typography
                      color='#ffffff'
                      sx={{
                        textTransform: 'none',
                        fontSize: '12px',
                        fontWeight: '600'
                      }}
                    >
                      View All Events
                    </Typography>
                  </Box>
                </Box>
              }
            />
          </Tabs>
        ) : (
          <Box
            width='100%'
            height='100%'
            sx={{ maxWidth: { lg: '632px', sm: '100%' } }}
          >
            <Box
              height='160px'
              style={flexcol}
              justifyContent='center'
              alignItems='center'
              sx={{
                maxWidth: { lg: '594px', sm: '100%' },
                padding: '24px, 161px, 24px, 161px',
                borderRadius: '20px',
                gap: '8px',
                backgroundColor: '#F7F7F7'
              }}
            >
              <Box
                width='48px'
                height='48px'
                bgcolor='rgba(30, 73, 226, 0.1)'
                style={flexItemCenter}
                borderRadius='3px'
              >
                <Box component='img' src={HelpIcon} alt='Help Icon' />
              </Box>
              <Typography
                align='right'
                sx={{
                  opacity: '0.5',
                  ml: -3,
                  mt: 3,
                  fontSize: '13px',
                  fontWeight: '450',
                  lineHeight: '18.2px',
                  width: '269px'
                }}
              >
                There's nothing here yet. Start your first request to
                populate your events queue.
              </Typography>
            </Box>
          </Box>
        )}
      </Grid>
    </Grid>
  );
};

export default CalendarEventSlider; 